import React, { useEffect, useRef } from 'react';

/* ── Intersection Observer hook for scroll reveals ── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-section ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* ── Section Wrapper ── */
const Section = ({ children, className = '', id }) => (
  <section id={id} className={`relative px-6 sm:px-10 md:px-16 lg:px-24 ${className}`}>
    <div className="max-w-[860px] mx-auto">{children}</div>
  </section>
);

/* ── Divider ── */
const Divider = () => (
  <div className="max-w-[860px] mx-auto px-6 sm:px-10 md:px-16 lg:px-24">
    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-4" />
  </div>
);

const BriefingPage = () => {
  useEffect(() => {
    document.title = 'Kindra Health — Briefing';
    document.body.style.backgroundColor = '#0a0a0a';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen selection:bg-white/20" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }} data-testid="briefing-page">

      <style>{`
        .reveal-section { opacity: 0; transform: translateY(24px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal-section.revealed { opacity: 1; transform: translateY(0); }
        .text-fade { color: #a3a3a3; }
        .text-bright { color: #f5f5f5; }
        .accent { color: #6ee7b7; }
        .flow-line { background: linear-gradient(90deg, transparent, rgba(110,231,183,0.3), transparent); height: 1px; }
      `}</style>

      {/* ═══ 1. OPENING ═══ */}
      <Section className="pt-24 sm:pt-32 pb-20 sm:pb-28">
        <Reveal>
          <div className="flex items-center gap-3 mb-12">
            <img src="/kindra-logo.png" alt="Kindra" className="h-8 object-contain opacity-80" />
          </div>
        </Reveal>
        <Reveal delay={200}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-bright leading-[1.15] tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            The world doesn't have<br className="hidden sm:block" /> a data problem.<br />
            <span className="text-white/40">It has a decision problem.</span>
          </h1>
        </Reveal>
        <Reveal delay={400}>
          <p className="text-base sm:text-lg text-fade leading-relaxed max-w-[580px]">
            Health information is everywhere. Test results, prescriptions, symptoms, histories, reports. 
            What's missing isn't data — it's knowing what actually matters, and what to do next.
          </p>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 2. THE PROBLEM ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">The reality</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
            People are managing their health with fragments.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
          {[
            { text: 'Symptoms accumulate but patterns go unnoticed.' },
            { text: 'Medications interact in ways nobody connects.' },
            { text: 'Appointments happen in 15-minute windows with incomplete context.' },
            { text: 'Between visits, nothing is watching, remembering, or helping.' },
            { text: 'People arrive at care underprepared — not because they don\'t care, but because the picture is scattered.' },
            { text: 'Clinicians are time-limited. The patient is the one who falls through the gap.' },
          ].map((item, i) => (
            <Reveal key={i} delay={150 + i * 80}>
              <p className="text-sm text-fade leading-relaxed pl-4 border-l border-white/8">{item.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Divider />

      {/* ═══ 3. THE SHIFT ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">The shift</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Kindra sits between your data<br className="hidden sm:block" /> and your decisions.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="text-sm text-fade leading-relaxed max-w-[520px] mb-14">
            Not another place to store information. A system that reads what's there, recognises what matters, and helps you act on it.
          </p>
        </Reveal>

        {/* Flow diagram */}
        <Reveal delay={300}>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-0 sm:justify-between mb-8">
            {[
              { label: 'Input', items: ['Symptoms', 'Medications', 'History', 'Reports', 'Changes over time'] },
              { label: 'Intelligence', items: ['Pattern recognition', 'Context linking', 'Priority detection', 'Risk awareness'] },
              { label: 'Action', items: ['What matters now', 'What to ask', 'Where to go next', 'How to prepare'] },
            ].map((col, i) => (
              <div key={col.label} className="flex-1 max-w-[240px]">
                <div className="flex items-center gap-3 mb-4">
                  {i > 0 && <div className="hidden sm:block w-8 h-px bg-white/10" />}
                  <span className={`text-xs uppercase tracking-[0.15em] font-semibold ${i === 1 ? 'accent' : 'text-white/50'}`}>{col.label}</span>
                </div>
                <div className="space-y-2">
                  {col.items.map(item => (
                    <p key={item} className="text-xs text-fade leading-relaxed">{item}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 4. WHAT KINDRA DOES ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">What it does</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
            It turns scattered health information<br className="hidden sm:block" /> into clearer next steps.
          </h2>
        </Reveal>
        <div className="space-y-6">
          {[
            { title: 'Combines context', desc: 'Symptoms, history, medications, and timing — brought together in one place and read as a whole.' },
            { title: 'Identifies patterns', desc: 'Recognises when things connect, change, or cluster in ways that matter — even when the user hasn\'t noticed yet.' },
            { title: 'Clarifies what matters', desc: 'Cuts through noise to surface what\'s actually relevant right now, not everything that could theoretically be relevant.' },
            { title: 'Supports better decisions', desc: 'Helps people walk into care more prepared — with the right questions, the right context, and clearer direction.' },
          ].map((item, i) => (
            <Reveal key={i} delay={100 + i * 100}>
              <div className="flex gap-5 items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-bright font-medium mb-1">{item.title}</p>
                  <p className="text-sm text-fade leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Divider />

      {/* ═══ 5. WHAT MAKES IT DIFFERENT ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">The difference</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Most tools store information.<br />
            <span className="text-white/40">Kindra interprets it.</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="p-6 rounded-2xl border border-white/6 bg-white/[0.02]">
              <p className="text-xs uppercase tracking-[0.15em] text-white/30 mb-4">Traditional tools</p>
              <div className="space-y-3">
                {['Store data', 'Log information', 'Show dashboards', 'Leave interpretation to the user'].map(item => (
                  <p key={item} className="text-sm text-white/30 leading-relaxed">{item}</p>
                ))}
              </div>
            </div>
            <div className="p-6 rounded-2xl border border-emerald-400/15 bg-emerald-400/[0.03]">
              <p className="text-xs uppercase tracking-[0.15em] accent mb-4">Kindra Health</p>
              <div className="space-y-3">
                {['Interprets context', 'Connects patterns', 'Prioritises what matters', 'Guides action'].map(item => (
                  <p key={item} className="text-sm text-bright leading-relaxed">{item}</p>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 6. REAL-WORLD SCENARIOS ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">In practice</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            What this looks like in real life.
          </h2>
        </Reveal>

        {/* Scenario 1: Burning mouth */}
        <Reveal delay={200}>
          <div className="p-6 sm:p-8 rounded-2xl border border-white/6 bg-white/[0.02] mb-8">
            <p className="text-xs uppercase tracking-[0.15em] accent mb-4">Scenario</p>
            <h3 className="text-lg text-bright font-light mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Persistent burning mouth — unclear pathway
            </h3>
            <div className="space-y-4 text-sm text-fade leading-relaxed">
              <p>
                A user has been dealing with a persistent burning sensation in their mouth for weeks. They're on several medications 
                for existing conditions. Their GP visits have been inconclusive — the symptom doesn't fit neatly into an obvious category.
              </p>
              <p>
                Kindra connects their symptom history, current medications, and broader health profile. It surfaces that this symptom 
                cluster — particularly alongside their medication list — may be relevant to oral medicine, and that a specialist referral 
                pathway exists that their GP may not have considered.
              </p>
              <p>
                It helps them understand that specific blood tests may support the referral, that oral medicine is the relevant 
                specialty, and what to bring to their next GP appointment to request the right pathway.
              </p>
              <p className="text-bright/80 pt-2 border-t border-white/6">
                The user arrives at care clearer, better prepared, and further ahead — with the right referral direction and 
                useful context already in hand.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Scenario 2: Fatigue patterns */}
        <Reveal delay={300}>
          <div className="p-6 sm:p-8 rounded-2xl border border-white/6 bg-white/[0.02]">
            <p className="text-xs uppercase tracking-[0.15em] accent mb-4">Scenario</p>
            <h3 className="text-lg text-bright font-light mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Persistent fatigue — scattered symptoms, no clear picture
            </h3>
            <div className="space-y-4 text-sm text-fade leading-relaxed">
              <p>
                A user has been dealing with fatigue, intermittent headaches, and poor sleep for months. They've mentioned it at 
                two different appointments but each time it was treated in isolation — sleep advice from one, a headache prescription from another.
              </p>
              <p>
                Over time, Kindra sees these as connected. It recognises that the fatigue, sleep disruption, and headache pattern 
                together with their medication timing may point to something worth investigating holistically rather than symptom by symptom.
              </p>
              <p className="text-bright/80 pt-2 border-t border-white/6">
                Before their next appointment, Kindra helps them prepare a clear picture — what's been happening, how things connect, 
                and what questions to raise. The clinician gets a more complete starting point in the first minute.
              </p>
            </div>
          </div>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 7. COMPOUNDING VALUE ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">Over time</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-2xl sm:text-3xl font-light text-bright leading-snug mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            It becomes more useful<br className="hidden sm:block" /> the longer it's used.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <div className="space-y-5 max-w-[560px]">
            <p className="text-sm text-fade leading-relaxed">
              Not because it stores more data for the sake of it — but because context deepens, patterns become clearer, and the 
              system learns what matters in this person's real life.
            </p>
            <p className="text-sm text-fade leading-relaxed">
              Signal strengthens over time. Relevance increases. The gap between "I have information" and 
              "I know what to do" gets smaller with every interaction.
            </p>
            <p className="text-sm text-fade leading-relaxed">
              This is what makes it different from tools that are equally useful on day one and day three hundred. 
              Kindra compounds.
            </p>
          </div>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 8. BIGGER VISION ═══ */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-white/30 mb-6">Beyond health</p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-xl sm:text-2xl font-light text-bright leading-snug mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Health is the first domain.<br />
            <span className="text-white/40">Not the only one.</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="text-sm text-fade leading-relaxed max-w-[520px]">
            The same intelligence pattern — combining scattered context, recognising what matters, and guiding clearer decisions — 
            applies wherever people face complex, ongoing decisions with incomplete information. 
            Health is where it starts. It is not where it ends.
          </p>
        </Reveal>
      </Section>

      <Divider />

      {/* ═══ 9. CLOSE ═══ */}
      <Section className="py-24 sm:py-32 pb-32 sm:pb-40">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-bright leading-snug mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            This is the beginning of a system designed<br className="hidden sm:block" /> to make complex decisions clearer.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="text-sm text-fade leading-relaxed max-w-[480px]">
            Built carefully. Grounded in real use. Designed to matter more over time.
          </p>
        </Reveal>
        <Reveal delay={400}>
          <div className="mt-16 flex items-center gap-3">
            <img src="/kindra-logo.png" alt="Kindra Health" className="h-6 object-contain opacity-50" />
          </div>
        </Reveal>
      </Section>

    </div>
  );
};

export default BriefingPage;
